// ============================================
// Modo de cor (claro/escuro): preferência persistida em localStorage,
// default seguindo prefers-color-scheme do sistema. Aplica o tema MUI.
// ============================================

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { APPBAR_BG, makeTheme, type ColorMode } from '../theme'

const STORAGE_KEY = 'raiz:color-mode'

interface ColorModeContextValue {
  mode: ColorMode
  toggle: () => void
  setMode: (mode: ColorMode) => void
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null)

const readInitial = (): ColorMode => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') return saved
  } catch {
    // localStorage indisponível (modo privado) — cai no sistema
  }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}

export const ColorModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ColorMode>(readInitial)

  const setMode = useCallback((next: ColorMode) => {
    setModeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignora falha de persistência
    }
  }, [])

  const toggle = useCallback(() => {
    setModeState((m) => {
      const next = m === 'light' ? 'dark' : 'light'
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // ignora
      }
      return next
    })
  }, [])

  // Segue o sistema enquanto o usuário não escolheu explicitamente.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (e: MediaQueryListEvent) => {
      let hasExplicit = false
      try {
        hasExplicit = localStorage.getItem(STORAGE_KEY) != null
      } catch {
        hasExplicit = false
      }
      if (!hasExplicit) setModeState(e.matches ? 'dark' : 'light')
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Barra de status do celular acompanha o AppBar do tema ativo (sensação de
  // app nativo; no desktop o meta é inócuo).
  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', APPBAR_BG[mode])
  }, [mode])

  const theme = useMemo(() => makeTheme(mode), [mode])
  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode])

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export const useColorMode = (): ColorModeContextValue => {
  const ctx = useContext(ColorModeContext)
  if (!ctx) throw new Error('useColorMode deve ser usado dentro de <ColorModeProvider>')
  return ctx
}
